import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Checkbox } from './ui/checkbox';
import {
  Mail, Loader2, Clock, Calendar, Sparkles, Paperclip, Save, Trash2, Send
} from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, authJsonFetch } from '../lib/api';
import { AttachmentList } from './AttachmentPreview';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;
const BATCH_SIZE = 50;

const chunkArray = (array, chunkSize) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

/**
 * Inline Bulk Email Campaign dialog used from the Data Preview footer.
 *
 * The previous flow (`navigate('/email-hub', { state: { prefillBulk: ... } })`)
 * suffered from a race where the default `limit=20` bulk-org fetch on the
 * Email Hub mount overlapped the prefill mutation, producing "276 recipients
 * selected" from a 16-org Data Preview. By doing the whole flow in a dialog
 * here, the recipient set is *exactly* what the operator passed in — no
 * other fetch ever runs against it.
 *
 * Posts to the same `/api/bulk-email` endpoint as Email Hub, splitting
 * recipients into batches of 50, so the existing scheduler picks the jobs
 * up unchanged.
 */
export const BulkEmailCampaignDialog = ({ open, onOpenChange, orgIds, initialName = '' }) => {
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recipients, setRecipients] = useState([]); // [{org_id, org_name, contact_name, contact_email, sector_name}]
  const [selectedIds, setSelectedIds] = useState(new Set()); // org_ids currently ticked
  const [search, setSearch] = useState('');

  const [campaignName, setCampaignName] = useState(initialName);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [campaignTemplates, setCampaignTemplates] = useState([]);
  const [savingCampaignTemplate, setSavingCampaignTemplate] = useState(false);
  const [campaignTemplateName, setCampaignTemplateName] = useState('');
  const [showSaveCampaignDialog, setShowSaveCampaignDialog] = useState(false);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  const [polishTone, setPolishTone] = useState('professional');
  const [polishing, setPolishing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Resolve org_ids → full recipient info on dialog open.
  // Operator request (Mar 2026): keep orgs that lack a primary contact
  // email VISIBLE in the recipients table (greyed out + un-selectable)
  // instead of silently dropping them. Previously 12 orgs would shrink
  // to "9 selected" with no visible explanation — looked like a bug.
  const [missingEmailCount, setMissingEmailCount] = useState(0);
  useEffect(() => {
    if (!open) return;
    if (!Array.isArray(orgIds) || orgIds.length === 0) {
      setRecipients([]);
      setSelectedIds(new Set());
      setMissingEmailCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingRecipients(true);
      try {
        const res = await authJsonFetch(`${API}/organizations/bulk-by-ids`, {
          method: 'POST',
          body: JSON.stringify({ org_ids: orgIds }),
        });
        if (!res.ok) {
          toast.error('Failed to load recipients');
          if (!cancelled) {
            setRecipients([]);
            setSelectedIds(new Set());
            setMissingEmailCount(0);
          }
          return;
        }
        const data = await res.json();
        const all = (data.organizations || []);
        const withEmail = all.filter(o => o.contact_email);
        const without = all.length - withEmail.length;
        if (!cancelled) {
          // Keep ALL orgs in the list — sendable ones first so the user
          // can see what's selectable, then the unsendable ones at the
          // bottom flagged "no email".
          const sortedAll = [
            ...withEmail,
            ...all.filter(o => !o.contact_email),
          ];
          setRecipients(sortedAll);
          // Default-select only those with a valid email
          setSelectedIds(new Set(withEmail.map(o => o.org_id)));
          setMissingEmailCount(without);
        }
      } catch {
        if (!cancelled) toast.error('Failed to load recipients');
      } finally {
        if (!cancelled) setLoadingRecipients(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, orgIds]);

  // Load templates + campaign templates once on open
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [tplRes, ctplRes] = await Promise.all([
          authFetch(`${API}/email-templates`),
          authFetch(`${API}/campaign-templates`),
        ]);
        if (tplRes.ok) setTemplates(await tplRes.json());
        if (ctplRes.ok) setCampaignTemplates(await ctplRes.json());
      } catch {
        /* non-fatal */
      }
    })();
  }, [open]);

  // Reset form when dialog closes
  useEffect(() => {
    if (open) return;
    setCampaignName(initialName);
    setSubject('');
    setBody('');
    setSelectedTemplateId('');
    setScheduledDate('');
    setScheduledTime('');
    setAttachments([]);
    setSearch('');
  }, [open, initialName]);

  const selectedRecipients = useMemo(
    () => recipients.filter(r => selectedIds.has(r.org_id)),
    [recipients, selectedIds],
  );

  const filteredRecipients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter(r =>
      (r.org_name || '').toLowerCase().includes(q) ||
      (r.contact_name || '').toLowerCase().includes(q) ||
      (r.contact_email || '').toLowerCase().includes(q) ||
      (r.sector_name || '').toLowerCase().includes(q)
    );
  }, [recipients, search]);

  const toggleRecipient = (orgId) => {
    // Can't email an org without an address — silently no-op so a stray
    // click on a greyed-out row doesn't pollute the selection.
    const row = recipients.find(r => r.org_id === orgId);
    if (!row || !row.contact_email) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  };

  const toggleAllVisible = () => {
    // Only the rows that actually have an email participate in the
    // "select all" toggle — the rest stay out of the selection.
    const visibleSendable = filteredRecipients.filter(r => r.contact_email).map(r => r.org_id);
    const allSelected = visibleSendable.length > 0 && visibleSendable.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) visibleSendable.forEach(id => next.delete(id));
      else visibleSendable.forEach(id => next.add(id));
      return next;
    });
  };

  const applyTemplate = (id) => {
    setSelectedTemplateId(id);
    if (!id) return;
    const t = templates.find(x => x.template_id === id);
    if (t) {
      setSubject(t.subject || '');
      setBody(t.body || '');
      if (Array.isArray(t.attachments) && t.attachments.length > 0) {
        setAttachments(t.attachments);
        toast.info(`${t.attachments.length} attachment(s) loaded from template`);
      }
    }
  };

  const applyCampaignTemplate = (id) => {
    const t = campaignTemplates.find(x => x.template_id === id);
    if (!t) return;
    setSubject(t.subject_template || '');
    setBody(t.body_template || '');
    setAttachments(t.attachments || []);
    toast.success(`Loaded campaign template: ${t.name}`);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const token = localStorage.getItem('gs_session_token');
        const res = await fetch(`${API}/attachments`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });
        if (res.ok) {
          const att = await res.json();
          setAttachments(prev => [...prev, att]);
          toast.success(`${file.name} attached`);
        } else {
          const err = await res.json().catch(() => ({}));
          toast.error(err.detail || `Failed to upload ${file.name}`);
        }
      } catch {
        toast.error(`Upload error: ${file.name}`);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(a => a.attachment_id !== attachmentId));
  };

  const handlePolish = async () => {
    if (!body) {
      toast.error('Add some body text first');
      return;
    }
    setPolishing(true);
    try {
      const res = await authJsonFetch(`${API}/ai/polish-email`, {
        method: 'POST',
        body: JSON.stringify({ text: body, tone: polishTone }),
      });
      if (res.ok) {
        const data = await res.json();
        setBody(data.polished_text || body);
        toast.success('Email polished');
      } else {
        toast.error('Failed to polish');
      }
    } catch {
      toast.error('Polish failed');
    } finally {
      setPolishing(false);
    }
  };

  const saveCampaignTemplate = async () => {
    if (!campaignTemplateName.trim()) {
      toast.error('Enter a template name');
      return;
    }
    setSavingCampaignTemplate(true);
    try {
      const res = await authJsonFetch(`${API}/campaign-templates`, {
        method: 'POST',
        body: JSON.stringify({
          name: campaignTemplateName,
          subject_template: subject,
          body_template: body,
          attachments,
        }),
      });
      if (res.ok) {
        const created = await res.json();
        setCampaignTemplates(prev => [created, ...prev]);
        setShowSaveCampaignDialog(false);
        setCampaignTemplateName('');
        toast.success('Campaign template saved');
      } else {
        toast.error('Failed to save template');
      }
    } catch {
      toast.error('Save error');
    } finally {
      setSavingCampaignTemplate(false);
    }
  };

  const deleteCampaignTemplate = async (id) => {
    if (!window.confirm('Delete this campaign template?')) return;
    try {
      const res = await authFetch(`${API}/campaign-templates/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setCampaignTemplates(prev => prev.filter(t => t.template_id !== id));
        toast.success('Campaign template deleted');
      } else {
        toast.error('Failed to delete');
      }
    } catch {
      toast.error('Delete error');
    }
  };

  const handleSubmit = async () => {
    if (selectedRecipients.length === 0) {
      toast.error('Select at least one recipient');
      return;
    }
    if (!campaignName.trim()) {
      toast.error('Enter a campaign name');
      return;
    }
    if (!subject.trim()) {
      toast.error('Enter a subject');
      return;
    }
    if (!body.trim()) {
      toast.error('Enter a body');
      return;
    }
    if (!scheduledDate || !scheduledTime) {
      toast.error('Set a scheduled date and time');
      return;
    }
    const scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`);
    if (scheduledAt < new Date(Date.now() + 2 * 60 * 60 * 1000)) {
      toast.error('Scheduled time must be at least 2 hours from now');
      return;
    }

    setSubmitting(true);
    try {
      // Same chunking semantics as Email Hub: split into batches of 50,
      // each becomes its own /bulk-email row.
      const batches = chunkArray(selectedRecipients, BATCH_SIZE);
      let okCount = 0;
      const errors = [];
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const batchName = batches.length > 1
          ? `${campaignName} (Batch ${i + 1}/${batches.length})`
          : campaignName;
        const res = await authJsonFetch(`${API}/bulk-email`, {
          method: 'POST',
          body: JSON.stringify({
            name: batchName,
            recipients: batch,
            template_id: selectedTemplateId || null,
            subject_template: subject,
            body_template: body,
            scheduled_at: scheduledAt.toISOString(),
            attachments,
          }),
        });
        if (res.ok) okCount++;
        else {
          const err = await res.json().catch(() => ({}));
          errors.push(`Batch ${i + 1}: ${err.detail || 'failed'}`);
        }
      }
      if (okCount === batches.length) {
        if (batches.length > 1) {
          toast.success(`Scheduled ${batches.length} batches for ${selectedRecipients.length} recipients`);
        } else {
          toast.success(`Scheduled campaign for ${selectedRecipients.length} recipients`);
        }
        onOpenChange(false);
      } else if (okCount > 0) {
        toast.warning(`Scheduled ${okCount}/${batches.length} batches. Errors: ${errors.join('; ')}`);
      } else {
        toast.error(`All batches failed: ${errors.join('; ')}`);
      }
    } catch (err) {
      toast.error('Failed to schedule campaign');
    } finally {
      setSubmitting(false);
    }
  };

  // Visible-and-sendable rows drive the header "select all" checkbox —
  // rows without email never participate.
  const visibleSendable = useMemo(
    () => filteredRecipients.filter(r => r.contact_email),
    [filteredRecipients],
  );
  const allVisibleSelected = visibleSendable.length > 0 &&
    visibleSendable.every(r => selectedIds.has(r.org_id));

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="bulk-email-campaign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Bulk Email Campaign
            </DialogTitle>
            <DialogDescription>
              Compose, schedule, and send to up to {orgIds?.length || 0} organisations from your filtered Data Preview.
              {missingEmailCount > 0 && (
                <> Only those with a primary contact email will receive the campaign — see the warning below.</>
              )} Recipients are split into batches of 50.
            </DialogDescription>
          </DialogHeader>

          {/* Recipients block */}
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold">
                Recipients <span className="text-muted-foreground font-normal">({selectedRecipients.length} of {recipients.length} selected)</span>
              </h3>
              <Input
                placeholder="Search by org / contact / sector / email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-xs h-8"
                data-testid="bulk-dialog-recipient-search"
              />
            </div>
            {/* Persistent warning when some orgs lack a primary contact email
                — operator request: don't silently drop them, make it obvious. */}
            {!loadingRecipients && missingEmailCount > 0 && (
              <div
                className="text-xs border border-amber-300 bg-amber-50 text-amber-900 rounded-md p-2 flex items-start gap-2"
                data-testid="bulk-dialog-missing-email-banner"
              >
                <span className="font-medium">⚠</span>
                <div>
                  <strong>{missingEmailCount} of {recipients.length}</strong> organisation{missingEmailCount === 1 ? '' : 's'}
                  {' '}{missingEmailCount === 1 ? "doesn't have" : "don't have"} a primary contact email and can't be included in this campaign.
                  They are shown greyed-out at the bottom of the list. Add a contact email to that organisation's record to include them.
                </div>
              </div>
            )}
            <div className="border rounded-md max-h-[260px] overflow-y-auto">
              {loadingRecipients ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading recipients…
                </div>
              ) : filteredRecipients.length === 0 ? (
                <div className="py-6 text-center text-muted-foreground text-sm">
                  {recipients.length === 0
                    ? 'No recipients with a primary contact email in the filtered Data Preview.'
                    : 'No recipients match your search.'}
                </div>
              ) : (
                <Table>
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allVisibleSelected}
                          onCheckedChange={toggleAllVisible}
                          data-testid="bulk-dialog-select-all"
                        />
                      </TableHead>
                      <TableHead>Organisation</TableHead>
                      <TableHead>Sector</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Email</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRecipients.map(r => {
                      const sendable = !!r.contact_email;
                      return (
                        <TableRow
                          key={r.org_id}
                          className={sendable ? "cursor-pointer" : "opacity-60 bg-muted/30 cursor-not-allowed"}
                          onClick={() => sendable && toggleRecipient(r.org_id)}
                          data-testid={`bulk-dialog-recipient-${r.org_id}`}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedIds.has(r.org_id)}
                              disabled={!sendable}
                              onCheckedChange={() => sendable && toggleRecipient(r.org_id)}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{r.org_name}</TableCell>
                          <TableCell>
                            {r.sector_name && (
                              <Badge variant="outline" className="text-xs">{r.sector_name}</Badge>
                            )}
                          </TableCell>
                          <TableCell>{r.contact_name || '—'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {sendable ? r.contact_email : (
                              <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50" data-testid="bulk-dialog-no-email-badge">
                                no email — can't send
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>

          {/* Campaign Settings */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2">
            {/* Left col */}
            <div className="space-y-4">
              <div>
                <Label>Campaign Name *</Label>
                <Input
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="e.g., Term 2 Photo Day Reminder"
                  data-testid="bulk-dialog-campaign-name"
                />
              </div>

              <div>
                <Label>Use Email Template (Optional)</Label>
                <Select value={selectedTemplateId || 'none'} onValueChange={(v) => applyTemplate(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="bulk-dialog-template-select">
                    <SelectValue placeholder="Select a template…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None — write custom</SelectItem>
                    {templates.map(t => (
                      <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-3 border rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Campaign Templates</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setCampaignTemplateName(''); setShowSaveCampaignDialog(true); }}
                    disabled={!subject && !body}
                    data-testid="bulk-dialog-save-campaign-tpl"
                  >
                    <Save className="w-3 h-3 mr-1" />
                    Save Current
                  </Button>
                </div>
                {campaignTemplates.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-2">No saved campaign templates yet</p>
                ) : (
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {campaignTemplates.map(ct => (
                      <div key={ct.template_id} className="flex items-center justify-between p-1.5 rounded border hover:bg-muted/50">
                        <button
                          type="button"
                          onClick={() => applyCampaignTemplate(ct.template_id)}
                          className="flex-1 text-left min-w-0"
                          data-testid={`bulk-dialog-load-campaign-${ct.template_id}`}
                        >
                          <p className="text-sm font-medium truncate">{ct.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{ct.subject_template || 'No subject'}</p>
                        </button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteCampaignTemplate(ct.template_id)}
                          className="text-destructive hover:text-destructive ml-1"
                          title="Delete template"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label>Subject *</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Email subject with {{org_name}}"
                  data-testid="bulk-dialog-subject"
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label>Body *</Label>
                  <div className="flex items-center gap-2">
                    <Select value={polishTone} onValueChange={setPolishTone}>
                      <SelectTrigger className="w-28 h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="formal">Formal</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={handlePolish} disabled={polishing || !body} className="h-7">
                      {polishing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                      AI Assist
                    </Button>
                  </div>
                </div>
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Dear {{contact_name}},&#10;&#10;Write your email content here…"
                  rows={8}
                  data-testid="bulk-dialog-body"
                  className="mt-2"
                />
              </div>

              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs font-medium mb-2">Available Merge Variables:</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="cursor-pointer" onClick={() => setBody(prev => prev + '{{org_name}}')}>{'{{org_name}}'}</Badge>
                  <Badge variant="outline" className="cursor-pointer" onClick={() => setBody(prev => prev + '{{contact_name}}')}>{'{{contact_name}}'}</Badge>
                  <Badge variant="outline" className="cursor-pointer" onClick={() => setBody(prev => prev + '{{email}}')}>{'{{email}}'}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Click a badge to insert at the end of the body.</p>
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  Attachments
                  {attachments.length > 0 && <Badge variant="secondary" className="text-xs">{attachments.length}</Badge>}
                </Label>
                <div className="mt-2 space-y-2">
                  <AttachmentList
                    attachments={attachments}
                    onRemove={removeAttachment}
                    showPreview={true}
                    emptyMessage="No attachments"
                  />
                  <input ref={fileInputRef} type="file" multiple onChange={handleFileUpload} className="hidden" />
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="bulk-dialog-add-attachment">
                    <Paperclip className="w-4 h-4 mr-2" />
                    Add Attachments
                  </Button>
                </div>
              </div>
            </div>

            {/* Right col */}
            <div className="space-y-4">
              <div className="p-4 border rounded-lg">
                <Label className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4" />
                  Schedule Send *
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Scheduled time must be at least 2 hours from now. Emails are sent with a 10-second interval per recipient.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Date</Label>
                    <Input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="bulk-dialog-schedule-date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Time</Label>
                    <Input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      data-testid="bulk-dialog-schedule-time"
                    />
                  </div>
                </div>
              </div>

              {selectedRecipients.length > 0 && subject && body && (
                <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                  <h4 className="font-medium text-green-800 dark:text-green-300 mb-2">Ready to Schedule</h4>
                  <ul className="text-sm text-green-700 dark:text-green-400 space-y-1">
                    <li>• {selectedRecipients.length} recipient{selectedRecipients.length === 1 ? '' : 's'} ({Math.ceil(selectedRecipients.length / BATCH_SIZE)} batch{selectedRecipients.length > BATCH_SIZE ? 'es of 50' : ''})</li>
                    <li>• Subject: {subject.substring(0, 50)}{subject.length > 50 ? '…' : ''}</li>
                    {scheduledDate && scheduledTime && <li>• Scheduled: {scheduledDate} at {scheduledTime} (Sydney)</li>}
                    {attachments.length > 0 && <li>• {attachments.length} attachment{attachments.length === 1 ? '' : 's'}</li>}
                  </ul>
                </div>
              )}

              <Button
                className="w-full"
                size="lg"
                onClick={handleSubmit}
                disabled={submitting || selectedRecipients.length === 0 || !subject || !body || !scheduledDate || !scheduledTime || !campaignName.trim()}
                data-testid="bulk-dialog-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {selectedRecipients.length > BATCH_SIZE
                  ? `Schedule ${selectedRecipients.length} Emails (${Math.ceil(selectedRecipients.length / BATCH_SIZE)} Batches)`
                  : `Schedule ${selectedRecipients.length || ''} Email${selectedRecipients.length === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-dialog-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Campaign Template inner dialog */}
      <Dialog open={showSaveCampaignDialog} onOpenChange={setShowSaveCampaignDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Save Campaign Template</DialogTitle>
            <DialogDescription>
              Save the current subject, body, and attachments as a reusable campaign template.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Template name</Label>
            <Input
              value={campaignTemplateName}
              onChange={(e) => setCampaignTemplateName(e.target.value)}
              placeholder="e.g., Photo Day Reminder"
              data-testid="bulk-dialog-save-tpl-name"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveCampaignDialog(false)}>Cancel</Button>
            <Button onClick={saveCampaignTemplate} disabled={savingCampaignTemplate || !campaignTemplateName.trim()}>
              {savingCampaignTemplate ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkEmailCampaignDialog;
